import ImageOptimize from '@/components/ImageOptimize'
import type {
  FlashSale as FlashSaleType,
  PromotionProductCarousel,
} from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { Tab } from '@headlessui/react'
import dynamic from 'next/dynamic'
import { type MouseEventHandler, useRef, useState } from 'react'

const ProductCarousel = dynamic(() => import('@/components/ProductCarousel'), {
  ssr: false,
})

interface ProductCarouselPanelProps {
  flashSale: FlashSaleType | null
  productCarousel: PromotionProductCarousel[]
}

export const ProductCarouselPanel = ({
  flashSale,
  productCarousel,
}: ProductCarouselPanelProps) => {
  const [selectedProductCarouselIndex, setSelectedProductCarouselIndex] =
    useState(0)
  const panelsRef = useRef<HTMLDivElement>(null)

  const handleChangeTab = (newIndex: number) => {
    document
      .getElementById(`product-carrousel-tab-${newIndex}`)
      ?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      })
    setSelectedProductCarouselIndex(newIndex)

    if (panelsRef.current) {
      const rect = panelsRef.current.getBoundingClientRect()
      const top = rect.top + (document.scrollingElement?.scrollTop || 0) - 84
      document.scrollingElement?.scrollTo({ behavior: 'smooth', top })
    }
  }

  const handleClickTab: MouseEventHandler<HTMLDivElement> = e => {
    e.currentTarget?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    })

    if (panelsRef.current) {
      const rect = panelsRef.current.getBoundingClientRect()
      const top = rect.top + (document.scrollingElement?.scrollTop || 0) - 84
      document.scrollingElement?.scrollTo({ behavior: 'smooth', top })
    }
  }

  const renderProductCarousels = [
    ...(flashSale ? [flashSale] : []),
    ...(productCarousel || []),
  ].filter(x => x.products?.length)

  return renderProductCarousels?.length ? (
    <Tab.Group
      vertical
      selectedIndex={selectedProductCarouselIndex}
      onChange={handleChangeTab}
      as='div'
      className='w-full rounded-xl'
    >
      <Tab.List className='sticky top-0 z-10 flex overflow-x-auto bg-white scrollbar-hide'>
        {renderProductCarousels?.map((e, i) => (
          <Tab
            key={i}
            as='div'
            id={`product-carrousel-tab-${i}`}
            onClick={handleClickTab}
            className='h-[84px] min-w-[243px] max-w-[243px] border-b-[3px] border-white outline-none ui-selected:border-[#FDE68A] ui-selected:bg-[#FFFBEB] ui-selected:font-bold'
          >
            <ImageOptimize
              src={e.image || e.customization?.titleIcon || ''}
              alt={getTranslate(e.description) || ''}
              className='h-full w-full object-cover'
            />
          </Tab>
        ))}
      </Tab.List>
      <Tab.Panels className='flex overflow-hidden' ref={panelsRef}>
        {renderProductCarousels?.map((e, i) => (
          <Tab.Panel key={i} className='w-full'>
            <ProductCarousel data={e} showCountdown={e === flashSale} />
          </Tab.Panel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  ) : null
}
